import { Component, PropTypes } from 'react';
import TimeTo from 'scripts/components/TimeTo';
import cx from 'classnames';
import _ from 'lodash';
import styles from 'styles/table';

export default class Table extends Component {
	static propTypes = {
		type: PropTypes.string.isRequired,
		content: PropTypes.oneOfType([
			PropTypes.object,
			PropTypes.array
		]).isRequired,
		contentHighlight: PropTypes.object,
		title: PropTypes.string,
		subtitle: PropTypes.string,
		collapsed: PropTypes.bool,
		tracking: PropTypes.string,
		onHeaderClick: PropTypes.func,
		onNestedExpand: PropTypes.func,
		children: PropTypes.oneOfType([
			PropTypes.arrayOf(PropTypes.node),
			PropTypes.node
		])
	}

	constructor(props) {
		super(props);

		this.state = {
			isExpanded: !props.collapsed,
			isNestedExpanded: false
		};

		this.handleHeaderClick = this.handleHeaderClick.bind(this);
		this.handleNestedExpandableClick = this.handleNestedExpandableClick.bind(this);
		this.renderTableHeadResult = this.renderTableHeadResult.bind(this);
		this.renderTableRowResult = this.renderTableRowResult.bind(this);
	}

	componentWillMount() {
		this._isVsResult = this.isVsResult();
		this._isIndividualResult = this.isIndividualResult();
		this._isCountryResult = this.isCountryResult();
	}

	isVsResult() {
		return this.props.content.length === 2
			&& _.every(this.props.content, (result) => result.hasOwnProperty('result'));
	}

	isIndividualResult() {
		return _.every(_.flatten(this.props.content), (result) => result.hasOwnProperty('name'));
	}

	isCountryResult() {
		return _.every(_.flatten(this.props.content), (result) => result.hasOwnProperty('country')
			&& !(result.hasOwnProperty('gold')
				&& result.hasOwnProperty('silver')
				&& result.hasOwnProperty('bronze')));
	}

	isExpandable() {
		return this.props.collapsed;
	}

	isNestedExpandable() {
		return !!this.props.content.results;
	}

	hasTimeTo() {
		return !!this.props.content.datetime;
	}

	hasHeaderLink() {
		return !!this.props.onHeaderClick
			&& !this.props.collapsed;
	}

	handleHeaderClick() {
		if (this.isExpandable()) {
			this.setState({isExpanded: !this.state.isExpanded});
		} else if (this.props.onHeaderClick) {
			this.props.onHeaderClick();
		}
	}

	handleNestedExpandableClick() {
		this.setState({isNestedExpanded: !this.state.isNestedExpanded}, () => {
			if (this.props.onNestedExpand && this.state.isNestedExpanded) {
				this.props.onNestedExpand();
			}
		});
	}

	renderTableHeader() {
		return !!this.props.title && (
			<div className={cx(styles.header, {[styles.expandable]: this.isExpandable(), [styles.isExpanded]: this.state.isExpanded, [styles.headerLink]: this.hasHeaderLink(), [styles.hasSubtitle]: !!this.props.subtitle})} onClick={this.handleHeaderClick}>
				<span className={styles.title}>{this.props.title}</span>

				{this.props.subtitle
					&& <span className={styles.subtitle}>{this.props.subtitle}</span>}

				{this.hasTimeTo()
					&& <TimeTo time={this.props.content.datetime} />}
			</div>
		);
	}

	renderTableNestedExpandable() {
		return !!this.props.children && (
			<div className={cx(styles.expandableContent, {[styles.isExpanded]: this.state.isNestedExpanded})}>
				{this.props.children}
			</div>
		);
	}

	renderTableContent() {
		switch (this.props.type) {
			case 'item':
				return this.renderTableItem();

			case 'accordian':
				return this.renderTableAccordian();

			case 'list':
				return this.renderTableList();
		}
	}

	renderTableItem() {
		return (
			<div className={styles.tableItem}>
				<div className={styles.tableTop}>
					<div className={cx(styles.iconDiscipline, styles[`iconDiscipline${this.props.content.discipline.replace(/\s/g, '')}`])} />

					{this.props.content.isMedalled
						&& <div className={styles.iconMedalled} />}

					<div className={styles.labelDiscipline}>{this.props.content.discipline}</div>
					<div className={styles.labelEvent}>{this.props.content.event} {this.props.content.phase ? `- ${this.props.content.phase}` : ''}</div>
				</div>

				<div className={cx(styles.tableBottom, {[styles.expandable]: this.isNestedExpandable(), [styles.isExpanded]: this.state.isNestedExpanded})} onClick={this.isNestedExpandable() && this.handleNestedExpandableClick}>
					<span className={cx(styles.iconLocation)} />
					<span className={styles.labelLocation}>{this.props.content.location}</span>
				</div>
			</div>
		);
	}

	renderTableAccordian() {
		return (
			<div className={styles.tableAccordian}>
				<div className={cx(styles.tableTop, styles.expandable, {[styles.isExpanded]: this.state.isNestedExpanded})} onClick={this.handleNestedExpandableClick}>
					<div className={cx(styles.iconDiscipline, styles[`iconDiscipline${this.props.content.discipline.replace(/\s/g, '')}`])} />
					<div className={styles.labelDiscipline}>{this.props.content.discipline}</div>
				</div>
			</div>
		);
	}

	renderTableList() {
		return (
			<div className={styles.tableList}>
				{this.props.contentHighlight
					&& this.renderTableHighlight()}

				{(this._isIndividualResult || this._isCountryResult
					? this.renderTableHeadResult
					: this.renderTableHeadMedal)()}

				<div className={styles.tableBottom}>
					{this.props.content.map((data, index) => {
						// If row is actually a double award for the same medal
						if (Array.isArray(data)) {
							return data.map((d, i) => this.renderTableRow(d, `${index},${i}`, true));
						}

						return this.renderTableRow(data, index);
					})}
				</div>
			</div>
		);
	}

	renderTableHeadResult() {
		return (
			<div className={styles.tableTop}>
				<span className={styles.titleTableHead}>Country</span>

				{this._isIndividualResult
					&& <span className={styles.titleTableHead}>Name</span>}

				<span className={styles.titleTableHead}>Result</span>
			</div>
		);
	}

	renderTableHeadMedal() {
		return (
			<div className={cx(styles.tableTop, styles.tableTopMedals)}>
				<span className={styles.titleTableHead}>Country</span>
				<span className={styles.titleTableHead}>Gold</span>
				<span className={styles.titleTableHead}>Silver</span>
				<span className={styles.titleTableHead}>Bronze</span>
				<span className={styles.titleTableHead}>Total</span>
			</div>
		);
	}

	renderTableRow(data, index, isDoubleMedal) {
		const countryFlagClass = styles[`iconCountry${data.country}`];

		// Ensure we only render rows where we have country flags
		// This is like a whitelist of olympic nations
		return !!countryFlagClass && (
			<div key={index} className={cx(styles.tableRow, {[styles.isVsResult]: this._isVsResult, [styles.isIndividualResult]: this._isIndividualResult, [styles.isCountryResult]: this._isCountryResult, [styles.isDoubleMedal]: isDoubleMedal, [styles.isHighlight]: data.isHighlight})} data-position={data.position}>
				<span className={cx(styles.iconCountry, countryFlagClass)} />
				<span className={styles.labelCountry}>{data.country}</span>

				{(this._isIndividualResult || this._isCountryResult
					? this.renderTableRowResult
					: this.renderTableRowMedal)(data)}
			</div>
		);
	}

	renderTableRowResult(data) {
		return (
			<div className={cx(styles.rowResult, {[styles.isWorldRecord]: data.isWorldRecord, [styles.isOlympicRecord]: data.isOlympicRecord})}>
				{this._isIndividualResult
					&& <span className={styles.labelName} title={data.name}>{data.name}</span>}

				<span className={styles.labelResult}>{data.result}</span>
			</div>
		);
	}

	renderTableRowMedal(data) {
		return (
			<div className={styles.rowResult}>
				<span className={cx(styles.labelCount, styles.labelCountGold)}>{data.gold}</span>
				<span className={cx(styles.labelCount, styles.labelCountSilver)}>{data.silver}</span>
				<span className={cx(styles.labelCount, styles.labelCountBronze)}>{data.bronze}</span>
				<span className={cx(styles.labelCount, styles.labelCountTotal)}>{data.gold + data.silver + data.bronze}</span>
			</div>
		);
	}

	renderTableHighlight() {
		return (
			<div className={styles.tableHighlight}>
				{this.renderTableRow(this.props.contentHighlight)}
			</div>
		);
	}

	render() {
		return (
			<div className={styles.table} data-tracking={this.props.tracking}>
				{this.renderTableHeader()}

				<div className={cx(styles.content, {[styles.expandableContent]: this.isExpandable(), [styles.isExpanded]: this.state.isExpanded})}>
					{this.renderTableContent()}
					{this.renderTableNestedExpandable()}
				</div>
			</div>
		);
	}
}



/** WEBPACK FOOTER **
 ** ./scripts/components/Table.es6
 **/